<template>
  <div class="vz-yandex-map">
    <client-only>
      <label class="vz-yandex-map-search">
        <input
          v-if="map && hasSearch"
          v-model="suggest"
          type="text"
          list="suggest"
          placeholder="Начните вводить для поиска"
          autocomplete="off"
        >
        <datalist id="suggest">
          <option v-for="(item, index) in suggestResponse ?? []" :key="index" :value="item.subtitle?.text">
            {{ item.title.text }}
          </option>
        </datalist>
      </label>
      <yandex-map
        v-model="map"
        :settings="{
          location: localLocation
      }"
        width="100%"
      >
        <slot name="map-additional" />
        <yandex-map-default-features-layer/>
        <yandex-map-listener />
        <yandex-map-default-scheme-layer />
        <yandex-map-controls :settings="{ position: 'right' }">
          <yandex-map-geolocation-control :settings="{}" />
        </yandex-map-controls>
        <slot>
          <vz-yandex-map-default-clusterer
            :has-tooltip="hasTooltip"
            :markers="markers"
            @marker-click="emit('marker-click', $event)"
          />
        </slot>
      </yandex-map>
    </client-only>
    <slot name="additional" />
  </div>
</template>

<script setup lang="ts">
import {
  YandexMap,
  YandexMapDefaultSchemeLayer,
  YandexMapDefaultFeaturesLayer,
  YandexMapListener,
  YandexMapControls,
  YandexMapGeolocationControl
} from 'vue-yandex-maps'
import { PropType } from 'vue'
import type {LngLat, YMap, YMapLocationRequest} from '@yandex/ymaps3-types';
import type { SuggestResponse } from '@yandex/ymaps3-types/imperative/suggest';
import {promiseTimeout} from '@vueuse/shared';
import VzYandexMapDefaultClusterer from '~/components/ui-kit/map/vz-yandex-map-default-clusterer.vue';
import {VzMapMarker} from '~/types/components/vz-yandex-map';

const emit = defineEmits([
  'update:modelValue',
  'update:location',
  'marker-click',
  'address-select',
])

const props = defineProps({
  modelValue: {
    type: Object as PropType<null | YMap>,
    default: null
  },
  location: {
    type: Object as PropType<YMapLocationRequest>,
    // default: () => ({   center: props.coords?.reverse() as LngLat, zoom: +props.zoom, })
  },
  markers: {
    type: Array as PropType<Array<VzMapMarker>>,
    default: () => []
  },
  hasTooltip: {
    type: Boolean,
    default: false
  },
  hasSearch: {
    type: Boolean,
    default: true
  },
})

const suggestResponse = shallowRef<null | SuggestResponse>(null)
const suggest = ref('')
const selectedSuggest = ref<LngLat | null>(null)

// const map = shallowRef<null | YMap>(null)
const map = computed({
  get() {
    return props.modelValue
  },
  set(value: any) {
    emit('update:modelValue', value)
  }
})

const localLocation = computed({
  get() {
    return props.location
  },
  set(value?: YMapLocationRequest) {
    emit('update:location', value)
  }
})

// const customization = shallowRef<VectorCustomization>([
//   {
//     tags: {
//       any: ['water'],
//     },
//     elements: 'geometry',
//     stylers: [
//       {
//         color: '#c2c2c2',
//       },
//     ],
//   },
//   {
//     tags: {
//       any: ['landscape', 'admin', 'land', 'transit'],
//     },
//     elements: 'geometry',
//     stylers: [
//       {
//         color: '#f1f1f1',
//       },
//     ],
//   },
//   {
//     tags: {
//       any: ['road'],
//     },
//     elements: 'geometry',
//     stylers: [
//       {
//         color: '#bdbdbd',
//       },
//     ],
//   },
//   {
//     tags: {
//       any: ['building'],
//     },
//     elements: 'geometry',
//     stylers: [
//       {
//         color: '#b6b6b6',
//       },
//     ],
//   },
// ]);

watch(suggest, async (val) => {
  if (!val) return;

  const existing = suggestResponse.value?.find((x) => x.subtitle?.text === val);
  if (existing) {
    const afterSuggestSearch = await ymaps3.search({
      text: val,
      type: ['toponyms'],
      bounds: map.value?.bounds,
    });
    if (afterSuggestSearch[0].geometry?.coordinates) {
      selectedSuggest.value = afterSuggestSearch[0].geometry.coordinates;
      return;
    }
  }

  await promiseTimeout(300);
  if (val !== suggest.value) return;
  suggestResponse.value = await ymaps3.suggest({
    text: val,
    bounds: map.value?.bounds,
    types: ['biz'],
  });
});

watch(selectedSuggest, async () => {
  if (selectedSuggest.value) {
    map.value?.setLocation({
      center: selectedSuggest.value,
      zoom: 15,
      duration: 300,
    });
    emit('address-select', selectedSuggest.value)
  }
});
</script>
